import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { userStorage } from '../../lib/userStorage';
import './FirstDashboardTour.css';

const TOUR_DONE_KEY = 'first_dashboard_walkthrough_complete';
const TOUR_ELIGIBLE_PREFIX = 'maicolleges:tour-eligible:';
const SEARCH_USED_EVENT = 'maicolleges:tour-college-search-used';
const COLLEGE_ADDED_EVENT = 'maicolleges:tour-college-added';

function getTargetRect(selector) {
  if (!selector) return null;
  const target = document.querySelector(selector);
  if (!target) return null;
  return target.getBoundingClientRect();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function FirstDashboardTour({ user, dashboards = [], sidebarOpen, setSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const firstDashboardPath = dashboards[0]?.path || '/dashboard/dashboard-1';

  const steps = useMemo(() => [
    {
      title: 'Start with one simple dashboard',
      body: 'For summer research, do not organize everything at once. Add a few colleges to one dashboard first, then improve it later.',
      actionLabel: 'Show me',
      prepare: () => navigate(firstDashboardPath),
    },
    {
      title: 'Open your dashboard menu',
      body: 'Click the highlighted menu button. This is where your dashboards live when you want to switch lists later.',
      selector: '[data-tour="sidebar-toggle"]',
      waitingText: 'Click the highlighted menu button to continue.',
      waitFor: () => sidebarOpen,
    },
    {
      title: 'Search for one college',
      body: 'Type a school you are curious about, then choose a match from the list. The tour will continue after you pick one.',
      selector: '[data-tour="college-search"]',
      waitingText: 'Choose a college from search to continue.',
      waitForEvent: SEARCH_USED_EVENT,
      prepare: () => {
        setSidebarOpen(false);
        navigate(firstDashboardPath);
      },
    },
    {
      title: 'Add it to your dashboard',
      body: 'Click Add now. Your first win is getting one school into one organized place.',
      selector: '[data-tour="add-college"]',
      waitingText: 'Click Add after choosing a school.',
      waitForEvent: COLLEGE_ADDED_EVENT,
      prepare: () => {
        setSidebarOpen(false);
        navigate(firstDashboardPath);
      },
    },
    {
      title: 'Use Progress later',
      body: 'Click Progress to see where final application schools, tasks, and deadlines will live later.',
      selector: '[data-tour="progress-nav"]',
      waitingText: 'Click Progress to finish the walkthrough.',
      waitFor: () => location.pathname === '/progress',
      prepare: () => setSidebarOpen(true),
    },
  ], [firstDashboardPath, location.pathname, navigate, setSidebarOpen, sidebarOpen]);

  const currentStep = steps[stepIndex];

  const completeTour = useCallback(() => {
    userStorage.setItem(TOUR_DONE_KEY, 'true');
    if (user?.uid) {
      window.localStorage.removeItem(`${TOUR_ELIGIBLE_PREFIX}${user.uid}`);
    }
    setActive(false);
    setStepIndex(0);
    setTargetRect(null);
  }, [user?.uid]);

  const startTour = useCallback(() => {
    setActive(true);
    setStepIndex(0);
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    if (userStorage.getItem(TOUR_DONE_KEY) === 'true') return;
    if (window.localStorage.getItem(`${TOUR_ELIGIBLE_PREFIX}${user.uid}`) !== 'true') return;
    const timer = window.setTimeout(startTour, 550);
    return () => window.clearTimeout(timer);
  }, [startTour, user?.uid]);

  useEffect(() => {
    const handleRestart = () => startTour();
    window.addEventListener('maicolleges:start-dashboard-tour', handleRestart);
    return () => window.removeEventListener('maicolleges:start-dashboard-tour', handleRestart);
  }, [startTour]);

  useEffect(() => {
    if (!active || !currentStep) return undefined;
    currentStep.prepare?.();

    const updateRect = () => {
      setTargetRect(getTargetRect(currentStep.selector));
    };

    const frame = window.requestAnimationFrame(updateRect);
    const retry = window.setTimeout(updateRect, 280);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(retry);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [active, currentStep, sidebarOpen]);

  useEffect(() => {
    if (!active || !currentStep?.waitForEvent) return undefined;
    const handleStepEvent = () => {
      setStepIndex((index) => Math.min(index + 1, steps.length - 1));
    };
    window.addEventListener(currentStep.waitForEvent, handleStepEvent);
    return () => window.removeEventListener(currentStep.waitForEvent, handleStepEvent);
  }, [active, currentStep, steps.length]);

  useEffect(() => {
    if (!active || currentStep?.waitForEvent === COLLEGE_ADDED_EVENT) return undefined;
    const handleCollegeAdded = () => {
      const addStepIndex = steps.findIndex((step) => step.waitForEvent === COLLEGE_ADDED_EVENT);
      if (addStepIndex === -1) return;
      setStepIndex((index) => (index <= addStepIndex ? Math.min(addStepIndex + 1, steps.length - 1) : index));
    };
    window.addEventListener(COLLEGE_ADDED_EVENT, handleCollegeAdded);
    return () => window.removeEventListener(COLLEGE_ADDED_EVENT, handleCollegeAdded);
  }, [active, currentStep, steps]);

  useEffect(() => {
    if (!active || !currentStep?.waitFor?.()) return;
    if (stepIndex >= steps.length - 1) {
      completeTour();
      return;
    }
    setStepIndex((index) => index + 1);
  }, [active, completeTour, currentStep, stepIndex, steps.length]);

  if (!active || !currentStep) return null;

  const hasTarget = Boolean(currentStep.selector && targetRect);
  const spotlightStyle = hasTarget
    ? {
        top: targetRect.top - 8,
        left: targetRect.left - 8,
        width: targetRect.width + 16,
        height: targetRect.height + 16,
      }
    : null;

  const cardStyle = hasTarget
    ? {
        top: clamp(targetRect.bottom + 16, 88, window.innerHeight - 260),
        left: clamp(targetRect.left, 18, window.innerWidth - 360),
      }
    : undefined;

  const handleNext = () => {
    if (currentStep.waitFor || currentStep.waitForEvent) return;
    if (stepIndex >= steps.length - 1) {
      completeTour();
      return;
    }
    setStepIndex((index) => index + 1);
  };

  return (
    <div className={`first-dashboard-tour ${currentStep.waitFor || currentStep.waitForEvent ? 'first-dashboard-tour--action' : ''}`} role="dialog" aria-modal="true" aria-labelledby="first-dashboard-tour-title">
      <div className="first-dashboard-tour__scrim" />
      {hasTarget && <div className="first-dashboard-tour__spotlight" style={spotlightStyle} />}
      <div className={`first-dashboard-tour__card ${hasTarget ? '' : 'first-dashboard-tour__card--center'}`} style={cardStyle}>
        {hasTarget && <div className="first-dashboard-tour__arrow" aria-hidden="true" />}
        <span className="first-dashboard-tour__step">Step {stepIndex + 1} of {steps.length}</span>
        <h2 id="first-dashboard-tour-title">{currentStep.title}</h2>
        <p>{currentStep.body}</p>
        {(currentStep.waitFor || currentStep.waitForEvent) && (
          <p className="first-dashboard-tour__waiting">{currentStep.waitingText || 'Complete the highlighted action to continue.'}</p>
        )}
        <div className="first-dashboard-tour__actions">
          <button type="button" className="first-dashboard-tour__skip" onClick={completeTour}>
            Skip
          </button>
          {!(currentStep.waitFor || currentStep.waitForEvent) && (
            <button type="button" className="first-dashboard-tour__next" onClick={handleNext}>
              {currentStep.actionLabel || 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FirstDashboardTour;
